import React, { useEffect, useState } from 'react';
import { AnalyticsType } from './types';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { fetchAnalytics, fetchFocusStats, setCurrentTab } from '../../../store/slices/analyticsSlice';
import { fetchAllProjects } from '../../../store/slices/projectsSlice';
import { TiChartBar } from 'react-icons/ti';
import { FaCalendarDay } from 'react-icons/fa';
import { GiProgression } from 'react-icons/gi';
import { HiChevronDown } from 'react-icons/hi';

import { formatHours } from '../../../utils/helpers';

const FocusAnalytics: React.FC = () => {
  const dispatch = useAppDispatch();
  const { data: analyticsData, stats, currentTab: analyticsTab, isLoading, isLoadingStats, error, statsError } = useAppSelector((state) => state.analytics);
  const { projects } = useAppSelector((state) => state.projects);
  const [selectedProjectId, setSelectedProjectId] = useState<string | undefined>(undefined);
  const [isProjectDropdownOpen, setIsProjectDropdownOpen] = useState(false);

  // Fetch projects on mount
  useEffect(() => {
    dispatch(fetchAllProjects());
  }, [dispatch]);

  // Fetch analytics data on mount and when tab or project changes
  useEffect(() => {
    dispatch(fetchAnalytics({ type: analyticsTab, projectId: selectedProjectId }));
  }, [dispatch, analyticsTab, selectedProjectId]);

  // Fetch focus stats on mount and when project changes
  useEffect(() => {
    dispatch(fetchFocusStats({ type: analyticsTab, projectId: selectedProjectId }));
  }, [dispatch, analyticsTab, selectedProjectId]);

  // Handle tab change
  const handleTabChange = (tab: AnalyticsType) => {
    dispatch(setCurrentTab(tab));
  };

  // Default stats if not loaded yet
  const displayStats = stats || {
    totalHours: 0,
    avgHoursPerSession: 0,
    totalSessions: 0,
    streak: 0,
    productivity: 0
  };

  // Calculate days focused (simplified - using streak as proxy)
  const daysFocused = displayStats.streak || 0;

  const selectedProject = selectedProjectId ? projects.find(p => p.id === selectedProjectId) : null;

  return (
    <div className="mt-12 sm:mt-16 relative z-10">
      <div className="text-center mb-8 sm:mb-10">
        <h3 className="text-3xl sm:text-4xl font-medium text-gray-900 mb-2">
          Focus Insights
        </h3>
        <p className="text-base text-gray-600">A quiet observation of your focus patterns</p>
      </div>

      <div className="bg-white rounded-2xl sm:rounded-3xl p-6 sm:p-8 md:p-10 border border-orange-100/40 shadow-sm">
        {/* Project Filter and Tab Selector */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mb-8">
          {/* Project Filter */}
          <div className="relative w-full sm:w-auto">
            <label className="block text-sm text-gray-600 mb-2">Filter by Project</label>
            <div className="relative">
              <button
                onClick={() => setIsProjectDropdownOpen(!isProjectDropdownOpen)}
                className="w-full sm:w-auto min-w-[200px] px-4 py-2.5 bg-white border border-gray-200/60 rounded-lg text-left flex items-center justify-between hover:border-orange-300/50 transition-all"
              >
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  {selectedProject ? (
                    <>
                      <div
                        className="w-3 h-3 rounded-full flex-shrink-0"
                        style={{ backgroundColor: selectedProject.color || '#f97316' }}
                      />
                      <span className="text-gray-900 font-normal truncate">{selectedProject.name}</span>
                    </>
                  ) : (
                    <span className="text-gray-400 font-normal">All Projects</span>
                  )}
                </div>
                <HiChevronDown className={`text-gray-400 transition-transform ${isProjectDropdownOpen ? 'rotate-180' : ''}`} />
              </button>

              {isProjectDropdownOpen && (
                <>
                  <div
                    className="fixed inset-0 z-10"
                    onClick={() => setIsProjectDropdownOpen(false)}
                  />
                  <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200/60 rounded-lg shadow-lg max-h-64 overflow-y-auto">
                    <div className="p-2">
                      <button
                        onClick={() => {
                          setSelectedProjectId(undefined);
                          setIsProjectDropdownOpen(false);
                        }}
                        className={`w-full px-3 py-2 text-left text-sm rounded-lg flex items-center gap-2 transition-colors ${!selectedProjectId
                          ? 'bg-orange-50 text-orange-600'
                          : 'text-gray-700 hover:bg-orange-50/40'
                          }`}
                      >
                        <span className="w-3 h-3 rounded-full border-2 border-gray-300"></span>
                        <span>All Projects</span>
                      </button>
                      {projects.map((project) => (
                        <button
                          key={project.id}
                          onClick={() => {
                            setSelectedProjectId(project.id);
                            setIsProjectDropdownOpen(false);
                          }}
                          className={`w-full px-3 py-2 text-left text-sm rounded-lg flex items-center gap-2 transition-colors outline-none focus:outline-none focus:ring-0 ${selectedProjectId === project.id
                            ? 'bg-orange-50 text-orange-600'
                            : 'text-gray-700 hover:bg-orange-50/40'
                            }`}
                        >
                          <div
                            className="w-3 h-3 rounded-full flex-shrink-0"
                            style={{ backgroundColor: project.color || '#f97316' }}
                          />
                          <span className="truncate flex-1"> {project.name}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Simple Tab Selector */}
          <div className="flex justify-center gap-2">
            {[
              { id: AnalyticsType.WEEKLY, label: 'Weekly', icon: <TiChartBar /> },
              { id: AnalyticsType.MONTHLY, label: 'Monthly', icon: <FaCalendarDay /> },
              { id: AnalyticsType.YEARLY, label: 'Yearly', icon: <GiProgression /> }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => handleTabChange(tab.id)}
                disabled={isLoading}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 flex items-center gap-2 ${analyticsTab === tab.id
                  ? 'bg-orange-50 text-orange-600 border border-orange-200/50'
                  : 'bg-transparent text-gray-600 border border-orange-100/40 hover:border-orange-200/60'
                  }`}
              >
                <span>{tab.icon}</span>
                <span>{tab.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Content Container */}
        <div className={`relative min-h-[300px] transition-opacity duration-300 ${isLoading ? 'opacity-60 pointer-events-none' : 'opacity-100'}`}>
          {isLoading && (
            <div className="absolute top-4 right-4 z-10">
              <div className="flex items-center gap-2 bg-white border border-orange-100 rounded-lg px-3 py-1.5">
                <div className="animate-spin rounded-full h-3 w-3 border-2 border-orange-400 border-t-transparent"></div>
                <span className="text-xs text-gray-600">Loading...</span>
              </div>
            </div>
          )}

          {error && !isLoading && (
            <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-4 text-center">
              <div className="text-sm text-red-600 mb-2">Error: {error}</div>
              <button
                onClick={() => dispatch(fetchAnalytics({ type: analyticsTab, projectId: selectedProjectId }))}
                className="text-orange-600 hover:text-orange-700 underline text-sm"
              >
                Retry
              </button>
            </div>
          )}

          {/* Weekly View */}
          {analyticsTab === AnalyticsType.WEEKLY && analyticsData.weekly && (
            <div className="space-y-6">
              <div className="bg-orange-50/30 rounded-lg p-4 border border-orange-100/60">
                <div className="text-right">
                  <div className="text-2xl font-medium text-gray-900">{displayStats.totalSessions}</div>
                  <div className="text-sm text-gray-600">Total Sessions</div>
                </div>
              </div>

              {/* Bar Chart */}
              <div className="bg-white rounded-lg p-6 border border-orange-100/60">
                <h4 className="text-base font-medium text-gray-700 mb-4">Your 7-Day Focus Summary</h4>
                <div className="flex items-end justify-between h-40 space-x-1">
                  {analyticsData.weekly.days.map((day, index) => (
                    <div key={index} className="flex flex-col items-center flex-1">
                      <div className="relative w-full">
                        <div
                          className="bg-orange-500/40 rounded-t w-full transition-all duration-1000 ease-out"
                          style={{
                            height: `${Math.max((day.hours / 4) * 100, 2)}%`,
                            minHeight: day.hours > 0 ? '2px' : '0px',
                          }}
                        />
                        {day.hours > 0 && (
                          <div className="absolute -top-6 left-1/2 transform -translate-x-1/2 text-xs text-gray-600 font-medium">
                            {formatHours(day.hours)}
                          </div>
                        )}
                      </div>
                      <div className="mt-2 text-xs text-gray-500">{day.day}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Monthly View */}
          {analyticsTab === AnalyticsType.MONTHLY && analyticsData.monthly && (
            <div className="space-y-6">
              <div className="bg-orange-50/30 rounded-lg p-4 border border-orange-100/60">
                <div className="text-right">
                  <div className="text-2xl font-medium text-gray-900">{displayStats.totalSessions}</div>
                  <div className="text-sm text-gray-600">Total Sessions</div>
                </div>
              </div>

              {/* Bar Chart */}
              <div className="bg-white rounded-lg p-6 border border-orange-100/60 overflow-x-auto">
                <h4 className="text-base font-medium text-gray-700 mb-4">Focus Hours This Month</h4>
                <div className="flex items-end justify-between h-40 space-x-0.5 min-w-[600px]">
                  {analyticsData.monthly.calendar.slice(0, 31).map((day, index) => (
                    <div key={index} className="flex flex-col items-center flex-1">
                      <div className="relative w-full">
                        <div
                          className="bg-orange-500/40 rounded-t w-full transition-all duration-1000 ease-out"
                          style={{
                            height: `${Math.max((day.hours / 4) * 100, 2)}%`,
                            minHeight: day.hours > 0 ? '2px' : '0px',
                          }}
                        />
                      </div>
                      <div className="mt-1 text-[10px] text-gray-400">{day.date}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Yearly View */}
          {analyticsTab === AnalyticsType.YEARLY && analyticsData.yearly && (
            <div className="space-y-6">
              <div className="bg-orange-50/30 rounded-lg p-4 border border-orange-100/60">
                <div className="text-right">
                  <div className="text-2xl font-medium text-gray-900">{displayStats.totalSessions}</div>
                  <div className="text-sm text-gray-600">Total Sessions</div>
                </div>
              </div>

              {/* Bar Chart */}
              <div className="bg-white rounded-lg p-6 border border-orange-100/60 overflow-x-auto">
                <h4 className="text-base font-medium text-gray-700 mb-4">Monthly Focus Hours</h4>
                <div className="flex items-end justify-between h-40 space-x-1 min-w-[600px]">
                  {analyticsData.yearly.months.map((month, index) => (
                    <div key={index} className="flex flex-col items-center flex-1">
                      <div className="relative w-full">
                        <div
                          className="bg-orange-500/40 rounded-t w-full transition-all duration-1000 ease-out"
                          style={{
                            height: `${Math.max((month.hours / 70) * 100, 2)}%`,
                            minHeight: month.hours > 0 ? '2px' : '0px',
                          }}
                        />
                        {month.hours > 0 && (
                          <div className="absolute -top-6 left-1/2 transform -translate-x-1/2 text-xs text-gray-600 font-medium">
                            {formatHours(month.hours)}
                          </div>
                        )}
                      </div>
                      <div className="mt-2 text-xs text-gray-500">{month.month}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Key Stats - Calm Metrics Only */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-8">
            <div className="bg-orange-50/30 rounded-lg p-4 text-center border border-orange-100/60">
              {isLoadingStats ? (
                <div className="animate-pulse">
                  <div className="h-6 bg-orange-100 rounded mb-2"></div>
                  <div className="h-4 bg-orange-100 rounded w-24 mx-auto"></div>
                </div>
              ) : (
                <>
                  <div className="text-2xl font-medium text-gray-900">{formatHours(displayStats.totalHours)}</div>
                  <div className="text-sm text-gray-600 mt-1">Total Focus Time</div>
                </>
              )}
            </div>
            <div className="bg-orange-50/30 rounded-lg p-4 text-center border border-orange-100/60">
              {isLoadingStats ? (
                <div className="animate-pulse">
                  <div className="h-6 bg-orange-100 rounded mb-2"></div>
                  <div className="h-4 bg-orange-100 rounded w-24 mx-auto"></div>
                </div>
              ) : (
                <>
                  <div className="text-2xl font-medium text-gray-900">{formatHours(displayStats.avgHoursPerSession)}</div>
                  <div className="text-sm text-gray-600 mt-1">Average Session Length</div>
                </>
              )}
            </div>
            <div className="bg-orange-50/30 rounded-lg p-4 text-center border border-orange-100/60">
              {isLoadingStats ? (
                <div className="animate-pulse">
                  <div className="h-6 bg-orange-100 rounded mb-2"></div>
                  <div className="h-4 bg-orange-100 rounded w-24 mx-auto"></div>
                </div>
              ) : (
                <>
                  <div className="text-2xl font-medium text-gray-900">{daysFocused}</div>
                  <div className="text-sm text-gray-600 mt-1">Days Focused</div>
                </>
              )}
            </div>
          </div>

          {statsError && !isLoadingStats && (
            <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-4 text-center">
              <div className="text-sm text-red-600 mb-2">Error loading stats: {statsError}</div>
              <button
                onClick={() => dispatch(fetchFocusStats({ type: analyticsTab, projectId: selectedProjectId }))}
                className="text-orange-600 hover:text-orange-700 underline text-sm"
              >
                Retry
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FocusAnalytics;
